import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Full-screen product media viewer.
 * One media item at a time, round arrows on the edges, and a thumbnail strip along the bottom.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - One slide per product media item.
 * @property {HTMLElement} [galleryList] - The horizontally scrolling slide track.
 * @property {HTMLElement} [thumbnails] - The horizontally scrolling thumbnail strip.
 * @property {HTMLButtonElement[]} [thumbnailButtons] - One button per thumbnail.
 * @property {HTMLElement} [counter] - Visually hidden "2 / 9" live region for screen readers.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();

  /** Index of the slide currently shown. */
  #activeIndex = 0;

  /** Element whose hover/touch/focus we watch to start downloading the full-size image early. */
  /** @type {HTMLElement | null} */
  #warmUpHost = null;

  connectedCallback() {
    super.connectedCallback();
    this.#track?.addEventListener('scroll', this.handleScroll, { passive: true });

    this.#warmUpHost = this.closest('media-gallery') ?? this.parentElement;
    for (const type of ['pointerover', 'touchstart', 'focusin']) {
      this.#warmUpHost?.addEventListener(type, this.#handleWarmUp, { passive: true });
    }

    // The image a shopper is most likely to open first: the selected variant's, or the first one.
    const warmFirst = () => this.#warmUp(this.closest('media-gallery')?.dataset.selectedMediaId) || this.#warmUpSlide(this.refs.media[0]);
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warmFirst, { timeout: 3000 });
    } else {
      setTimeout(warmFirst, 1500);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#track?.removeEventListener('scroll', this.handleScroll);

    for (const type of ['pointerover', 'touchstart', 'focusin']) {
      this.#warmUpHost?.removeEventListener(type, this.#handleWarmUp);
    }
    this.#warmUpHost = null;
  }

  /**
   * Hovering, touching or focusing a gallery image starts loading its full-size
   * version in the viewer, so it's ready by the time the click lands.
   * @param {Event} event
   */
  #handleWarmUp = (event) => {
    if (!(event.target instanceof Element) || this.contains(event.target)) return;
    const trigger = event.target.closest('[data-media-id]');
    if (trigger instanceof HTMLElement) this.#warmUp(trigger.dataset.mediaId);
  };

  /**
   * @param {string | undefined} mediaId
   * @returns {boolean} whether a matching slide was found
   */
  #warmUp(mediaId) {
    if (!mediaId) return false;
    const slide = this.refs.media.find((item) => item.dataset.mediaId === mediaId);
    return this.#warmUpSlide(slide);
  }

  /**
   * @param {HTMLElement | undefined} slide
   * @returns {boolean}
   */
  #warmUpSlide(slide) {
    const image = slide?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;
    if (image.loading === 'lazy') image.loading = 'eager';
    return true;
  }

  /**
   * Waits (briefly) until the viewer's copy of an image is downloaded and decoded,
   * so the open animation moves a real picture instead of an empty box.
   *
   * @param {HTMLElement | undefined} slide
   * @param {number} [maxWait] - Never hold the click longer than this (ms).
   * @returns {Promise<boolean>} true if the image is ready
   */
  async #imageReady(slide, maxWait = 350) {
    const image = slide?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return true;

    if (image.loading === 'lazy') image.loading = 'eager';
    if (image.complete && image.naturalWidth > 0) return true;

    let ready = false;
    await Promise.race([
      image.decode().then(() => (ready = true), () => {}),
      new Promise((resolve) => setTimeout(resolve, maxWait)),
    ]);
    return ready;
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.refs.galleryList ?? this.querySelector('.dialog-zoomed-gallery');
  }

  /** @returns {ScrollBehavior} */
  get #scrollBehavior() {
    return prefersReducedMotion() ? 'instant' : 'smooth';
  }

  /**
   * Opens the zoom dialog on a given media item.
   *
   * @param {number|string} index - The index of the media to show.
   * @param {Event} [event] - The triggering event.
   */
  async open(index, event) {
    event?.preventDefault();

    const { dialog, media } = this.refs;
    const activeIndex = this.#toIndex(index);
    const targetSlide = media[activeIndex];

    const show = () => {
      dialog.showModal();
      this.#scrollTrackTo(activeIndex, 'instant');
      this.#setActive(activeIndex, 'instant');
    };

    /** @type {HTMLElement | null} */
    const sourceElement = event?.target instanceof Element ? event.target.closest('li, slideshow-slide') : null;

    // Morph into the picture itself rather than the full-width slide, so the transition lines up.
    /** @type {HTMLElement | undefined} */
    const targetElement = targetSlide?.querySelector('.product-media') ?? targetSlide;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceElement || !targetElement) {
      show();
    } else {
      // Don't start the animation with a picture that hasn't downloaded yet.
      const imageReady = await this.#imageReady(targetSlide);
      const root = document.documentElement;

      // If it's still loading, cross-fade from the page image instead of flying an empty box.
      if (!imageReady) root.dataset.zoomImagePending = '';

      const itemTransitionName = 'gallery-item-open';
      sourceElement.style.setProperty('view-transition-name', itemTransitionName);

      const focalPoint = sourceElement.dataset.focalPoint;
      if (focalPoint) {
        root.style.setProperty('--gallery-media-focal-point', focalPoint);
      }

      await startViewTransition(() => {
        show();
        sourceElement.style.removeProperty('view-transition-name');
        targetElement.style.setProperty('view-transition-name', itemTransitionName);
      });

      root.style.removeProperty('--gallery-media-focal-point');
      delete root.dataset.zoomImagePending;
      targetElement.style.removeProperty('view-transition-name');
    }

    this.loadHighResolutionImage(targetSlide);
  }

  /**
   * Loads a high-resolution image for a specific media container.
   * @param {HTMLElement | undefined} mediaContainer - The media container element.
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
    return true;
  }

  /**
   * Runs when the slide track stops scrolling (swipe, trackpad, or programmatic).
   * Syncs the thumbnail strip and the page gallery to whatever slide is now showing.
   */
  handleScroll = debounce(() => {
    if (!this.refs.dialog?.open) return;

    const index = this.#getCurrentIndex();
    if (index !== this.#activeIndex) this.#setActive(index);

    this.loadHighResolutionImage(this.refs.media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (!dialog.open) return;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#getCurrentIndex();
    const activeSlide = media[activeIndex];

    /** @type {HTMLElement | undefined} */
    const sourceElement = activeSlide?.querySelector('.product-media') ?? activeSlide;
    const itemTransitionName = 'gallery-item-close';

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide || !sourceElement) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    // Fade the buttons and thumbnails out first so they aren't part of the transition snapshot.
    dialog.classList.add('dialog--closed');
    const closeButton = this.querySelector('.dialog-zoomed-gallery__close-button');
    if (closeButton) await onAnimationEnd(closeButton);

    sourceElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      sourceElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Escape closes; left/right arrow keys change image.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (this.refs.media.length < 2) return;

    event.preventDefault();

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== isRtl;

    if (forward) {
      this.navigateNext();
    } else {
      this.navigatePrev();
    }
  }

  /**
   * Show the previous media item (wraps to the last).
   */
  navigatePrev() {
    this.#step(-1);
  }

  /**
   * Show the next media item (wraps to the first).
   */
  navigateNext() {
    this.#step(1);
  }

  /**
   * Called from a thumbnail button.
   * @param {number|string} index
   */
  handleThumbnailClick(index) {
    this.selectThumbnail(index);
  }

  /**
   * Preloads the high-res image when hovering a thumbnail.
   * @param {number|string} index
   */
  handleThumbnailPointerEnter(index) {
    this.loadHighResolutionImage(this.refs.media[this.#toIndex(index)]);
  }

  /**
   * Shows the given media item: scrolls the track, highlights its thumbnail,
   * and tells the page gallery which item is active.
   *
   * @param {number|string} index
   * @param {{ behavior?: ScrollBehavior }} [options]
   */
  selectThumbnail(index, { behavior = this.#scrollBehavior } = {}) {
    const activeIndex = this.#toIndex(index);

    this.#scrollTrackTo(activeIndex, behavior);
    this.#setActive(activeIndex, behavior);
    this.loadHighResolutionImage(this.refs.media[activeIndex]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }

  /**
   * Preloads the image at the given index (called via on:pointerenter on product cards).
   * @param {number|string} index
   */
  async preloadImage(index) {
    this.loadHighResolutionImage(this.refs.media[this.#toIndex(index)]);
  }

  /**
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #step(direction) {
    const count = this.refs.media.length;
    if (count < 2) return;

    this.selectThumbnail((this.#activeIndex + direction + count) % count);
  }

  /**
   * Marks the active thumbnail, centres it in the strip, and updates the live counter.
   *
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #setActive(index, behavior = this.#scrollBehavior) {
    this.#activeIndex = index;

    const { counter, thumbnails, media } = this.refs;
    const thumbnailButtons = this.refs.thumbnailButtons ?? [];

    if (counter) counter.textContent = `${index + 1} / ${media.length}`;

    thumbnailButtons.forEach((button, i) => {
      button.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    const activeThumbnail = thumbnailButtons[index];
    if (activeThumbnail && thumbnails) centerInScroller(activeThumbnail, thumbnails, behavior);
  }

  /**
   * Scrolls the slide track so the given slide fills it. Works in LTR and RTL.
   *
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollTrackTo(index, behavior) {
    const track = this.#track;
    const slide = this.refs.media[index];
    if (!track || !slide) return;

    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (Math.abs(delta) < 1) return;

    // Absolute scrollTo (not scrollBy) so scroll-snap lands on the destination slide
    // instead of stopping at the next snap point on long jumps.
    track.scrollTo({ left: track.scrollLeft + delta, behavior });
  }

  /**
   * The slide that currently occupies most of the track.
   * @returns {number}
   */
  #getCurrentIndex() {
    const track = this.#track;
    const { media } = this.refs;
    if (!track) return this.#activeIndex;

    const trackRect = track.getBoundingClientRect();
    let bestIndex = this.#activeIndex;
    let bestVisible = 0;

    media.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const visible = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);

      if (visible > bestVisible) {
        bestVisible = visible;
        bestIndex = i;
      }
    });

    return bestIndex;
  }

  /**
   * Declarative events pass the index as a string; clamp it to a valid slide.
   * @param {number|string} value
   * @returns {number}
   */
  #toIndex(value) {
    const index = Number(value);
    const last = Math.max(this.refs.media.length - 1, 0);

    if (!Number.isInteger(index)) return 0;
    return Math.min(Math.max(index, 0), last);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Horizontally centres an element inside its scrolling ancestor without
 * touching the page's (or the dialog's) scroll position.
 *
 * @param {HTMLElement} element
 * @param {HTMLElement} scroller
 * @param {ScrollBehavior} behavior
 */
function centerInScroller(element, scroller, behavior) {
  const scrollerRect = scroller.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const delta = elementRect.left + elementRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);

  if (Math.abs(delta) < 1) return;
  scroller.scrollTo({ left: scroller.scrollLeft + delta, behavior });
}